import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import type { ProfileMenuUsage } from '@insio/ui-toolkit';
import { GetCurrentBillingDocument, GetProfileDocument } from '@/graphql';
import { formatQuotaResetDate, toQuotaInfo } from '@/features/admin/utils/quota-utils';
import { isMasterContext, useAuth } from '@/features/auth';
import {
  CreditViewer,
  creditViewerOf,
  toCreditMenuUsage,
  useCreditUsage
} from '@/features/credits';

/**
 * Usage and current plan for the desktop profile menu: the payer's credits on a credit plan,
 * the extraction quota on a document plan. Mirrors what System settings → Plan shows.
 *
 * @param planHref - Route to the billing settings; omit to render the plan as plain text.
 */
export function useMenuUsage(planHref?: string): ProfileMenuUsage | null {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { data: profileData } = useQuery(GetProfileDocument, { fetchPolicy: 'cache-first' });
  const { creditUsage, loading: creditUsageLoading } = useCreditUsage();
  const viewer = creditViewerOf(creditUsage);

  // A managed client has no figures of its own, and the board has no account to carry a quota —
  // there only a credit-paying master account has figures to show.
  const quota =
    viewer === CreditViewer.NONE
      ? toQuotaInfo(profileData?.profile?.account?.extractionQuota)
      : null;
  const showsUsage = !creditUsageLoading && (viewer === CreditViewer.PAYER || quota !== null);
  // The board's plan query may be refused to a master accountant, who gets no plan link either.
  const { data: billingData } = useQuery(GetCurrentBillingDocument, {
    fetchPolicy: 'cache-first',
    skip: !showsUsage || (isMasterContext(user) && !planHref)
  });

  if (!showsUsage) return null;

  const planName = billingData?.currentSubscription?.price?.plan?.name ?? null;

  if (creditUsage && viewer === CreditViewer.PAYER) {
    return toCreditMenuUsage(creditUsage, { locale: i18n.language, planName, planHref });
  }

  if (!quota) return null;

  return {
    used: quota.used,
    limit: quota.limit,
    resetDate: formatQuotaResetDate(quota, i18n.language),
    planName,
    planHref
  };
}
